import { RecordBucket } from "@scayla/api-client-fetch";
import { useAuth, useOidcStore } from "@scayla/core";
import type {
  RouteComponent,
  RouteLocationNormalized,
  RouteLocationNormalizedLoaded,
  RouteRecordRaw,
} from "vue-router";
import { createRouter, createWebHistory } from "vue-router";

import { $t } from "@/i18n";
import { localizeBuiltinModule } from "@/i18n/helpers";
import { useWorkspace } from "@/store/workspace";

import AppView from "./views/AppView.vue";
import LogoutRedirectView from "./views/LogoutRedirectView.vue";
import NotFoundView from "./views/NotFoundView.vue";
import OidcCallbackView from "./views/OidcCallbackView.vue";

export enum Routes {
  HOME = "home",
  NOT_FOUND = "notFound",
  SIGN_OUT = "signOut",
  LOGOUT_REDIRECT = "logoutRedirect",
  OIDC_CALLBACK = "oidcCallback",
  AI_PROMPT_ADMIN = "aiPromptAdmin",
  MODULE = "module",
  BIN = "bin",
  RECORD = "record",
  RECORD_CREATE = "recordCreate",
  RECORD_BULK_EDIT = "recordBulkEdit",
  RECORD_DETAILS = "recordDetails",
  RECORD_DOCUMENT_FLOW = "recordDocumentFlow",
  RECORD_RELATIONS = "recordRelations",
  RECORD_HISTORY = "recordHistory",
  RECORD_ACCESS_RIGHTS = "recordAccessRights",
  RECORD_HANDOVERS = "recordHandovers",
  RECORD_PRODUCT_PRICE = "recordProductPrice",
  RECORD_PRINT = "recordPrint",
  RECORD_FILTER_DETAILS = "recordFilterUpdate",
  RECORD_FILTER_DETAILS_CREATE = "recordFilterDetailsCreate",
  RECORD_FILTER_VISIBILITY = "recordFilterVisibility",
  RECORD_FILTER_VISIBILITY_CREATE = "recordFilterVisibilityCreate",
  CUSTOM_FIELDS = "customFields",
  CUSTOM_FIELD_DETAILS = "customFieldDetails",
  CUSTOM_FIELD_DETAILS_CREATE = "customFieldDetailsCreate",
  CUSTOM_FIELD_LOCATED_IN_FILTER_CREATE = "customFieldLocatedInFilterCreate",
  CUSTOM_FIELD_LOCATED_IN_FILTER_UPDATE = "customFieldLocatedInFilterUpdate",
  CUSTOM_FIELDS_FILTER_CREATE = "customFieldsFilterCreate",
  CUSTOM_FIELDS_FILTER_UPDATE = "customFieldsFilterUpdate",
  FIELD_GROUPS = "fieldGroups",
  FIELD_GROUP_DETAILS = "fieldGroupDetails",
  FIELD_GROUP_DETAILS_CREATE = "fieldGroupDetailsCreate",
  FIELD_GROUP_FILTER_CREATE = "fieldGroupFilterCreate",
  FIELD_GROUP_FILTER_UPDATE = "fieldGroupFilterUpdate",
  FOLDER_DETAILS = "folderDetails",
  FOLDER_DETAILS_CREATE = "folderDetailsCreate",
  TEMPLATES = "templates",
  TEMPLATE_DETAILS = "templateDetails",
  TEMPLATE_DETAILS_CREATE = "templateDetailsCreate",
  TEMPLATES_FILTER_CREATE = "templatesFilterCreate",
  TEMPLATES_FILTER_UPDATE = "templatesFilterUpdate",
  LAYOUT_SECTION_GROUPS = "layoutSectionGroups",
  LAYOUT_SECTION_GROUP_DETAILS = "layoutSectionGroupDetails",
  LAYOUT_SECTION_GROUP_CREATE = "layoutSectionGroupCreate",
  LAYOUT_SECTION_GROUP_UPDATE = "layoutSectionGroupUpdate",
  LAYOUT_SECTION_GROUP_FILTER_CREATE = "layoutSectionGroupFilterCreate",
  LAYOUT_SECTION_GROUP_FILTER_UPDATE = "layoutSectionGroupFilterUpdate",
  LAYOUT_SECTION_GROUP_LOCATED_IN_FILTER_CREATE = "layoutSectionGroupLocatedInFilterCreate",
  LAYOUT_SECTION_GROUP_LOCATED_IN_FILTER_UPDATE = "layoutSectionGroupLocatedInFilterUpdate",
  SETTINGS = "settings",
  SETTINGS_ACCOUNTING = "accountingSettings",
  SETTINGS_ORGANISATIONS = "organisationsSettings",
  SETTINGS_MODULE = "moduleSettings",
  SETTINGS_PERMISSIONS = "settingsPermissions",
  SETTINGS_PERMISSIONS_ROLES = "settingsPermissionsRoles",
  SETTINGS_PERMISSIONS_ROLES_DETAILS = "settingsPermissionsRolesDetails",
  SETTINGS_PERMISSIONS_ROLES_CREATE = "settingsPermissionsRolesCreate",
  SETTINGS_PERMISSIONS_ROLES_FILTER_CREATE = "settingsPermissionsRolesFilterCreate",
  SETTINGS_PERMISSIONS_ROLES_FILTER_UPDATE = "settingsPermissionsRolesFilterUpdate",
  SETTINGS_PERMISSIONS_ROLE_2_USR_GRP = "settingsPermissionsRole2UsrGrp",
  SETTINGS_PERMISSIONS_ROLE_2_USR_GRP_USER_DETAILS = "settingsPermissionsRole2UsrGrpUserDetails",
  SETTINGS_PERMISSIONS_ROLE_2_USR_GRP_USERGROUP_DETAILS = "settingsPermissionsRole2UsrGrpUsergroupDetails",
  SETTINGS_PERMISSIONS_ROLE_2_USR_GRP_FILTER_CREATE = "settingsPermissionsRole2UsrGrpFilterCreate",
  SETTINGS_PERMISSIONS_ROLE_2_USR_GRP_FILTER_UPDATE = "settingsPermissionsRole2UsrGrpFilterUpdate",
  SETTINGS_USERS = "usersSettings",
  SETTINGS_AI = "settingsAi",
  SETTINGS_AI_COMMON = "settingsAiCommon",
  SETTINGS_AI_FILE_CLASSIFICATION = "settingsAiFileClassification",
  SETTINGS_SERVICES = "settingsServices",
  SETTINGS_SERVICE_INTEGRATION = "settingsServiceIntegration",
  SETTINGS_SERVICE_INTEGRATION_DETAILS = "settingsServiceIntegrationDetails",
  SETTINGS_SERVICE_INTEGRATION_DETAILS_CREATE = "settingsServiceIntegrationDetailsCreate",
  SETTINGS_SERVICE_INTEGRATION_FILTER_CREATE = "settingsServiceIntegrationFilterCreate",
  SETTINGS_SERVICE_INTEGRATION_FILTER_UPDATE = "settingsServiceIntegrationFilterUpdate",
  SETTINGS_SERVICE_ACCOUNT = "settingsServiceAccount",
  SETTINGS_SERVICE_ACCOUNT_DETAILS = "settingsServiceAccountDetails",
  SETTINGS_SERVICE_ACCOUNT_DETAILS_CREATE = "settingsServiceAccountDetailsCreate",
  SETTINGS_SERVICE_ACCOUNT_FILTER_CREATE = "settingsServiceAccountFilterCreate",
  SETTINGS_SERVICE_ACCOUNT_FILTER_UPDATE = "settingsServiceAccountFilterUpdate",
  SETTINGS_SERVICE_RESOURCE_DETAILS = "settingsServiceResourceDetails",
  SETTINGS_SERVICE_RESOURCE_DETAILS_CREATE = "settingsServiceResourceDetailsCreate",
  SETTINGS_SERVICE_ACCOUNT_RESOURCES_FILTER_CREATE = "settingsServiceAccountResourcesFilterCreate",
  SETTINGS_SERVICE_ACCOUNT_RESOURCES_FILTER_UPDATE = "settingsServiceAccountResourcesFilterUpdate",
  SETTINGS_SERVICE_INTEGRATION_RESOURCE_DETAILS_CREATE = "settingsServiceIntegrationResourceDetailsCreate",
  SETTINGS_SERVICE_INTEGRATION_RESOURCE_DETAILS = "settingsServiceIntegrationResourceDetails",
  SETTINGS_SERVICE_INTEGRATION_RESOURCES_FILTER_CREATE = "settingsServiceIntegrationResourcesFilterCreate",
  SETTINGS_SERVICE_INTEGRATION_RESOURCES_FILTER_UPDATE = "settingsServiceIntegrationResourcesFilterUpdate",
  SETTINGS_SERVICE_INTEGRATION_ACCOUNTS_FILTER_CREATE = "settingsServiceIntegrationAccountsFilterCreate",
  SETTINGS_SERVICE_INTEGRATION_ACCOUNTS_FILTER_UPDATE = "settingsServiceIntegrationAccountsFilterUpdate",
  USER_DETAILS = "userDetails",
  USER_DETAILS_CREATE = "userDetailsCreate",
  USER_FILTER_CREATE = "userFilterCreate",
  USER_FILTER_UPDATE = "userFilterUpdate",
  USER_ASSIGNED_USER_GROUPS_FILTER_CREATE = "userAssignedUserGroupsFilterCreate",
  USER_ASSIGNED_USER_GROUPS_FILTER_UPDATE = "userAssignedUserGroupsFilterUpdate",
  USER_INDIVIDUAL_PERMISSIONS_FILTER_CREATE = "userIndividualPermissionsFilterCreate",
  USER_INDIVIDUAL_PERMISSIONS_FILTER_UPDATE = "userIndividualPermissionsFilterUpdate",
  SETTINGS_CUSTOM_DATA = "customData",
  CUSTOM_SEQUENCES = "customSequences",
  CUSTOM_SEQUENCE_DETAILS = "customSequenceDetails",
  CUSTOM_SEQUENCE_DETAILS_CREATE = "customSequenceDetailsCreate",
  CUSTOM_SEQUENCE_FILTER_CREATE = "customSequenceFilterCreate",
  CUSTOM_SEQUENCE_FILTER_UPDATE = "customSequenceFilterUpdate",
  CUSTOM_SEQUENCE_LOCATED_IN_FILTER_CREATE = "customSequenceLocatedInFilterCreate",
  CUSTOM_SEQUENCE_LOCATED_IN_FILTER_UPDATE = "customSequenceLocatedInFilterUpdate",
  TENANT = "tenant",
  TENANT_DETAILS = "tenantDetails",
  TENANT_ACCOUNTING = "tenantAccounting",
  TENANT_ACCOUNTING_TAX_RATES = "tenantAccountingTaxRates",
  TENANT_PUBLIC_CATEGORIES = "tenantPublicCategories",
  TENANT_PUBLIC_CATEGORY_DETAILS = "tenantPublicCategoryDetails",
  TENANT_PUBLIC_CATEGORY_DETAILS_CREATE = "tenantPublicCategoryDetailsCreate",
  TENANT_PUBLIC_CATEGORY_FILTER_CREATE = "tenantPublicCategoryFilterCreate",
  TENANT_PUBLIC_CATEGORY_FILTER_UPDATE = "tenantPublicCategoryFilterUpdate",
  TENANT_RECORD_STATUS_SETS = "tenantRecordStatusSets",
  TENANT_RECORD_STATUS_SET_DETAILS = "tenantRecordStatusSetDetails",
  TENANT_RECORD_STATUS_SET_DETAILS_CREATE = "tenantRecordStatusSetDetailsCreate",
  TENANT_RECORD_STATUS_SET_FILTER_CREATE = "tenantRecordStatusSetFilterCreate",
  TENANT_RECORD_STATUS_SET_FILTER_UPDATE = "tenantRecordStatusSetFilterUpdate",
  TENANT_RECORD_STATUS_SET_CONFIGURATION_FILTER_CREATE = "tenantRecordStatusSetConfigurationFilterCreate",
  TENANT_RECORD_STATUS_SET_CONFIGURATION_FILTER_UPDATE = "tenantRecordStatusSetConfigurationFilterUpdate",
  TENANT_RECORD_STATUS_SET_LOCATED_IN_FILTER_CREATE = "tenantRecordStatusSetLocatedInFilterCreate",
  TENANT_RECORD_STATUS_SET_LOCATED_IN_FILTER_UPDATE = "tenantRecordStatusSetLocatedInFilterUpdate",
  TENANT_RECORD_PRIORITY_SETS = "tenantRecordPrioritySets",
  TENANT_RECORD_PRIORITY_SET_DETAILS = "tenantRecordPrioritySetDetails",
  TENANT_RECORD_PRIORITY_SET_DETAILS_CREATE = "tenantRecordPrioritySetDetailsCreate",
  TENANT_RECORD_PRIORITY_SET_FILTER_CREATE = "tenantRecordPrioritySetFilterCreate",
  TENANT_RECORD_PRIORITY_SET_FILTER_UPDATE = "tenantRecordPrioritySetFilterUpdate",
  TENANT_RECORD_PRIORITY_SET_CONFIGURATION_FILTER_CREATE = "tenantRecordPrioritySetConfigurationFilterCreate",
  TENANT_RECORD_PRIORITY_SET_CONFIGURATION_FILTER_UPDATE = "tenantRecordPrioritySetConfigurationFilterUpdate",
  TENANT_RECORD_PRIORITY_SET_LOCATED_IN_FILTER_CREATE = "tenantRecordPrioritySetLocatedInFilterCreate",
  TENANT_RECORD_PRIORITY_SET_LOCATED_IN_FILTER_UPDATE = "tenantRecordPrioritySetLocatedInFilterUpdate",
  ORGANISATION = "organisation",
  ORGANISATIONS = "organisations",
  ORGANISATION_DETAILS = "organisationDetails",
  ORGANISATION_DETAILS_CREATE = "organisationDetailsCreate",
  ORGANISATION_FILTER_CREATE = "organisationFilterCreate",
  ORGANISATION_FILTER_UPDATE = "organisationFilterUpdate",
  ACCOUNTING = "accounting",
  ACCOUNTING_JOURNAL = "accountingJournal",
  USER_SETTINGS = "userSettings",
  USER_SETTINGS_PROFILE = "userSettingsProfile",
  USER_SETTINGS_PRIVATE_CATEGORIES = "userSettingsPrivateCategories",
  USER_SETTINGS_PRIVATE_CATEGORY_DETAILS = "userSettingsPrivateCategoryDetails",
  USER_SETTINGS_PRIVATE_CATEGORY_DETAILS_CREATE = "userSettingsPrivateCategoryDetailsCreate",
  USER_SETTINGS_PRIVATE_CATEGORY_FILTER_CREATE = "userSettingsPrivateCategoryFilterCreate",
  USER_SETTINGS_PRIVATE_CATEGORY_FILTER_UPDATE = "userSettingsPrivateCategoryFilterUpdate",
  TABLE_BAR = "tableBar",
  USER_GROUPS = "userGroups",
  USER_GROUP_DETAILS = "userGroupDetails",
  USER_GROUP_DETAILS_CREATE = "userGroupDetailsCreate",
  USER_GROUP_FILTER_CREATE = "userGroupFilterCreate",
  USER_GROUP_FILTER_UPDATE = "userGroupFilterUpdate",
  TENANT_MANAGEMENT = "tenantManagements",
  TENANT_MANAGEMENT_DETAILS = "tenantManagementDetails",
  TENANT_MANAGEMENT_DETAILS_CREATE = "tenantManagementDetailsCreate",
  TENANT_MANAGEMENT_DETAILS_COPY = "tenantManagementDetailsCopy",
  TENANT_MANAGEMENT_FILTER_CREATE = "tenantManagementFilterCreate",
  TENANT_MANAGEMENT_FILTER_UPDATE = "tenantManagementFilterUpdate",
  FILE_EDITOR = "fileEditor",
  FILE_PREVIEW = "filePreview",
  RECORD_FILE_EDITOR = "recordFileEditor",
  TEMPLATE_FILE_EDITOR = "templateFileEditor",
}

/**
 * The create/update route pair every settings list's filterset toolbar needs, sharing structure
 * across the ~18 lists that have one — the only per-list differences are the list's own route
 * names, its base path, its list component (and, for two lists, its own list props), and any extra
 * detail-view props beyond `returnTo`/`filterId`.
 */
function filterRoutes(cfg: {
  createName: Routes;
  updateName: Routes;
  basePath: string;
  listView: () => Promise<RouteComponent>;
  listProps?: (route: RouteLocationNormalized) => Record<string, unknown>;
  extraProps?: (route: RouteLocationNormalized) => Record<string, unknown>;
}): RouteRecordRaw[] {
  const shared = (route: RouteLocationNormalized) => ({
    returnTo: route.query.returnTo ?? undefined,
    ...cfg.extraProps?.(route),
  });
  return [
    {
      name: cfg.createName,
      path: `${cfg.basePath}/filter/create`,
      components: {
        tableLayoutColumn: cfg.listView,
        detailsLayoutColumn: () => import("./views/record/filter/FilterCreate.vue"),
      },
      props: {
        tableLayoutColumn: cfg.listProps ?? true,
        detailsLayoutColumn: shared,
      },
      meta: {
        rightBar: true,
        breadcrumb: $t("common.action.createFilter"),
      },
    },
    {
      name: cfg.updateName,
      path: `${cfg.basePath}/filter/:filterId`,
      components: {
        tableLayoutColumn: cfg.listView,
        detailsLayoutColumn: () => import("./views/record/filter/FilterUpdate.vue"),
      },
      props: {
        tableLayoutColumn: cfg.listProps ?? true,
        detailsLayoutColumn: (route) => ({ ...shared(route), filterId: route.params.filterId }),
      },
      meta: {
        rightBar: true,
        breadcrumb: $t("common.label.configureFilter"),
      },
    },
  ];
}

/**
 * The list shown beside a filter form. A filterset created from the Bin must keep the Bin in the left
 * column, not the module's live records — `returnTo` is what says which of the two the form came from.
 */
function filterFormListColumn(route: RouteLocationNormalizedLoaded) {
  const returnTo = typeof route.query.returnTo === "string" ? route.query.returnTo : undefined;
  const origin = returnTo ? router.resolve(returnTo) : undefined;
  const originModuleId = origin?.params.moduleId;
  const moduleId =
    (typeof originModuleId === "string" ? originModuleId : undefined) ?? (route.params.moduleId as string);
  const fromBin = origin?.name === Routes.BIN;
  return { moduleId, ...(fromBin ? { bucket: RecordBucket.DELETED } : {}) };
}

function binTrail(route: RouteLocationNormalizedLoaded): string[] {
  const binLabel = $t("trashBin.nav.label");
  const module = useWorkspace().modules?.find((m) => m.id === route.params.moduleId);
  return module ? [binLabel, localizeBuiltinModule(module.builtinModule)] : [binLabel];
}

const routes: RouteRecordRaw[] = [
  {
    name: Routes.SIGN_OUT,
    path: "/sign-out",
    redirect: { name: Routes.HOME },
    meta: { public: true },
  },
  {
    name: Routes.LOGOUT_REDIRECT,
    path: "/logout-redirect",
    component: LogoutRedirectView,
  },
  {
    name: Routes.OIDC_CALLBACK,
    path: "/oidc-callback",
    component: OidcCallbackView,
    meta: { public: true },
  },
  {
    path: "/",
    component: AppView,
    children: [
      {
        name: Routes.HOME,
        path: "/",
        components: {
          tableLayoutColumn: () => import("./views/HomeView.vue"),
        },
        props: {
          tableLayoutColumn: true,
        },
      },
      {
        name: Routes.MODULE,
        path: "/module/:moduleId",
        components: {
          tableLayoutColumn: () => import("./views/record/ModuleView.vue"),
        },
        props: {
          tableLayoutColumn: true,
        },
        meta: {
          // TODO: get name dynamically from moduleId
          breadcrumb: $t("common.label.module"),
        },
      },
      {
        name: Routes.BIN,
        path: "/bin/:moduleId",
        components: {
          tableLayoutColumn: () => import("./views/record/ModuleView.vue"),
        },
        props: {
          tableLayoutColumn: (route) => ({ moduleId: route.params.moduleId as string, bucket: RecordBucket.DELETED }),
        },
        meta: {
          breadcrumb: (route: RouteLocationNormalizedLoaded) => binTrail(route),
        },
      },
      {
        name: Routes.RECORD_FILTER_DETAILS_CREATE,
        path: "/module/:moduleId/filter/create",
        // path: "/module/:moduleId/filter/create?tenantId=:tenantId",
        components: {
          tableLayoutColumn: () => import("./views/record/ModuleView.vue"),
          detailsLayoutColumn: () => import("./views/record/filter/FilterCreate.vue"),
        },
        props: {
          tableLayoutColumn: (route) => filterFormListColumn(route),
          detailsLayoutColumn: (route) => ({
            moduleId: route.params.moduleId,
            returnTo: route.query.returnTo ?? undefined,
          }),
        },
        meta: {
          rightBar: true,
          breadcrumb: $t("common.action.createFilter"),
        },
        children: [
          {
            name: Routes.RECORD_FILTER_VISIBILITY_CREATE,
            path: "visibility",
            component: () => import("./views/record/filter/FilterVisibility.vue"),
            props: (route) => ({
              returnTo: route.query.returnTo ?? undefined,
            }),
            meta: {
              rightBar: true,
              breadcrumb: $t("record.filter.visibility.label"),
            },
          },
        ],
      },
      {
        name: Routes.RECORD_FILTER_DETAILS,
        path: "/module/:moduleId/filter/:filterId",
        components: {
          tableLayoutColumn: () => import("./views/record/ModuleView.vue"),
          detailsLayoutColumn: () => import("./views/record/filter/FilterUpdate.vue"),
        },
        props: {
          tableLayoutColumn: (route) => filterFormListColumn(route),
          detailsLayoutColumn: (route) => ({
            moduleId: route.params.moduleId,
            filterId: route.params.filterId,
            returnTo: route.query.returnTo ?? undefined,
          }),
        },
        meta: {
          rightBar: true,
          breadcrumb: $t("common.label.configureFilter"),
        },
        children: [
          {
            name: Routes.RECORD_FILTER_VISIBILITY,
            path: "visibility",
            component: () => import("./views/record/filter/FilterVisibility.vue"),
            props: (route) => ({
              returnTo: route.query.returnTo ?? undefined,
            }),
            meta: {
              rightBar: true,
              breadcrumb: $t("record.filter.visibility.label"),
            },
          },
        ],
      },
      {
        name: Routes.RECORD_CREATE,
        path: "/module/:moduleId/template/:templateId/create",
        components: {
          tableLayoutColumn: () => import("./views/record/ModuleView.vue"),
          detailsLayoutColumn: () => import("./views/record/RecordCreate.vue"),
        },
        props: {
          tableLayoutColumn: true,
          detailsLayoutColumn: true,
        },
        meta: {
          rightBar: true,
          breadcrumb: $t("common.action.create"),
        },
      },
      {
        name: Routes.RECORD_BULK_EDIT,
        path: "/module/:moduleId/template/:templateId/bulk-edit",
        components: {
          tableLayoutColumn: () => import("./views/record/ModuleView.vue"),
          detailsLayoutColumn: () => import("./views/record/bulkEdit/RecordBulkEdit.vue"),
        },
        props: {
          tableLayoutColumn: true,
          detailsLayoutColumn: true,
        },
        meta: {
          rightBar: true,
          breadcrumb: $t("record.bulkEdit.label.title"),
        },
      },
      {
        name: Routes.RECORD,
        path: "/module/:moduleId/:recordId",
        components: {
          tableLayoutColumn: () => import("./views/record/ModuleView.vue"),
          detailsLayoutColumn: () => import("./views/record/RecordView.vue"),
        },
        redirect: { name: Routes.RECORD_DETAILS },
        props: {
          tableLayoutColumn: (route) => {
            let moduleId = route.params.moduleId;
            if (route.query.returnTo) {
              const returnTo = route.query.returnTo as string;
              const match = /^\/module\/([^/]+)/.exec(returnTo);
              if (match) {
                moduleId = match[1];
              }
            }
            const bucket = route.query.bucket === RecordBucket.DELETED ? RecordBucket.DELETED : undefined;
            return { moduleId, bucket };
          },
          detailsLayoutColumn: (route) => ({
            moduleId: route.params.moduleId,
            recordId: route.params.recordId,
            returnTo: route.query.returnTo ?? undefined,
          }),
        },
        meta: {
          rightBar: true,
          breadcrumb: (route: RouteLocationNormalizedLoaded) =>
            route.query.bucket === RecordBucket.DELETED
              ? [...binTrail(route), $t("common.label.record")]
              : $t("common.label.record"),
        },
        children: [
          {
            name: Routes.RECORD_DETAILS,
            path: "",
            component: () => import("./views/record/RecordDetails.vue"),
            props: true,
            meta: {
              rightBar: true,
              previewBar: true,
            },
          },
          {
            name: Routes.RECORD_HISTORY,
            path: "history",
            component: () => import("./views/record/RecordHistory.vue"),
            props: true,
            meta: {
              rightBar: true,
              breadcrumb: $t("common.label.history"),
            },
          },
          {
            name: Routes.RECORD_ACCESS_RIGHTS,
            path: "accessRights",
            component: () => import("./views/record/accessRights/RecordAccessRights.vue"),
            props: true,
            meta: {
              rightBar: true,
              breadcrumb: $t("common.label.permissions"),
            },
          },
          {
            // One route for all four price tabs; the tab is the `priceType` param.
            name: Routes.RECORD_PRODUCT_PRICE,
            path: "price/:priceType",
            component: () => import("./views/record/RecordProductPriceTab.vue"),
            props: true,
            meta: {
              rightBar: true,
              breadcrumb: $t("common.label.prices"),
            },
          },
          {
            name: Routes.RECORD_RELATIONS,
            path: "relations",
            component: () => import("./views/record/recordRelations/RecordRelations.vue"),
            props: true,
            meta: {
              rightBar: true,
              breadcrumb: $t("common.label.relations"),
            },
          },
          {
            name: Routes.RECORD_HANDOVERS,
            path: "handovers",
            component: () => import("./views/record/RecordHandovers.vue"),
            props: true,
            meta: {
              rightBar: true,
              breadcrumb: $t("common.label.handovers"),
            },
          },
          {
            name: Routes.RECORD_DOCUMENT_FLOW,
            path: "documentFlow",
            component: () => import("./views/record/RecordDocumentFlow.vue"),
            props: true,
            meta: {
              rightBar: true,
              breadcrumb: $t("common.label.dossierFlow"),
            },
          },
          {
            name: Routes.RECORD_FILE_EDITOR,
            path: "file/:fileId/editor",
            component: () => import("./views/OnlyOfficeEditorView.vue"),
            props: (route) => ({
              fileId: route.params.fileId as string,
              embedded: true,
              mode: route.query.mode as string | undefined,
              recordId: route.params.recordId as string,
              generation: route.query.generation as string | undefined,
              fromTemplatesFolder: !!route.query.fromTemplatesFolder,
            }),
            meta: {
              rightBar: true,
              breadcrumb: $t("fileExplorer.editor.label.title"),
            },
          },
        ],
      },
      {
        path: "/accounting",
        name: Routes.ACCOUNTING,
        meta: {
          breadcrumb: $t("common.label.accounting"),
        },
        children: [
          {
            name: Routes.ACCOUNTING_JOURNAL,
            path: "/accounting/journal",
            components: {
              tableLayoutColumn: () => import("./views/accounting/JournalView.vue"),
            },
            props: {
              tableLayoutColumn: true,
            },
            meta: {
              breadcrumb: $t("common.label.journal"),
            },
          },
        ],
      },
      {
        path: "/tenants",
        name: Routes.TENANT_MANAGEMENT,
        components: {
          tableLayoutColumn: () => import("./views/tenantmanagement/TenantsView.vue"),
        },
        props: {
          tableLayoutColumn: true,
        },
        meta: {
          breadcrumb: $t("common.label.mandators"),
        },
      },
      {
        name: Routes.TENANT_MANAGEMENT_DETAILS,
        path: "/tenants/:tenantId",
        components: {
          tableLayoutColumn: () => import("./views/tenantmanagement/TenantsView.vue"),
          detailsLayoutColumn: () => import("./views/tenantmanagement/TenantDetails.vue"),
        },
        props: {
          tableLayoutColumn: true,
          detailsLayoutColumn: true,
        },
        meta: {
          rightBar: true,
          breadcrumb: $t("common.label.tenantDetails"),
        },
      },
      {
        name: Routes.TENANT_MANAGEMENT_DETAILS_CREATE,
        path: "/tenants/create",
        components: {
          tableLayoutColumn: () => import("./views/tenantmanagement/TenantsView.vue"),
          detailsLayoutColumn: () => import("./views/tenantmanagement/TenantDetailsCreate.vue"),
        },
        props: {
          tableLayoutColumn: true,
          detailsLayoutColumn: true,
        },
        meta: {
          rightBar: true,
          breadcrumb: $t("common.label.tenantCreate"),
        },
      },
      {
        name: Routes.TENANT_MANAGEMENT_DETAILS_COPY,
        path: "/tenants/:tenantId/copy",
        components: {
          tableLayoutColumn: () => import("./views/tenantmanagement/TenantsView.vue"),
          detailsLayoutColumn: () => import("./views/tenantmanagement/TenantDetailsCopy.vue"),
        },
        props: {
          tableLayoutColumn: true,
          detailsLayoutColumn: true,
        },
        meta: {
          rightBar: true,
          breadcrumb: $t("tenantManagement.copy.label.dialogTitle"),
        },
      },
      ...filterRoutes({
        createName: Routes.TENANT_MANAGEMENT_FILTER_CREATE,
        updateName: Routes.TENANT_MANAGEMENT_FILTER_UPDATE,
        basePath: "/tenants",
        listView: () => import("./views/tenantmanagement/TenantsView.vue"),
      }),
      {
        path: "/settings",
        name: Routes.SETTINGS,
        meta: {
          breadcrumb: $t("common.label.settings"),
        },
        children: [
          {
            name: Routes.SETTINGS_ACCOUNTING,
            path: "accounting",
            meta: {
              breadcrumb: $t("common.label.accounting"),
            },
            children: [
              {
                name: Routes.TENANT,
                path: "tenant",
                meta: {
                  breadcrumb: $t("common.label.mandator"),
                },
                children: [
                  // TODO: Uncomment when tax rates view is implemented
                  // {
                  //   name: Routes.TENANT_ACCOUNTING_TAX_RATES,
                  //   path: "tax-rates",
                  //   components: {
                  //     tableLayoutColumn: () => import("./views/settings/tenant/accounting/TaxRatesView.vue"),
                  //   },
                  //   props: {
                  //     tableLayoutColumn: true,
                  //   },
                  //   meta: {
                  //     breadcrumb: $t('common.label.taxRates'),
                  //   },
                  // },
                ],
              },
            ],
          },
          {
            name: Routes.SETTINGS_ORGANISATIONS,
            path: "organisations",
            components: {
              tableLayoutColumn: () => import("./views/settings/organisations/OrganizationsView.vue"),
            },
            props: {
              tableLayoutColumn: true,
            },
            meta: {
              breadcrumb: $t("common.label.organizations"),
            },
          },
          {
            name: Routes.TENANT_DETAILS,
            path: "organisations/tenant/:id",
            components: {
              tableLayoutColumn: () => import("./views/settings/organisations/OrganizationsView.vue"),
              detailsLayoutColumn: () => import("./views/settings/organisations/TenantDetails.vue"),
            },
            props: {
              tableLayoutColumn: true,
              detailsLayoutColumn: true,
            },
            meta: {
              rightBar: true,
              breadcrumb: $t("common.label.mandator"),
            },
          },
          {
            name: Routes.ORGANISATION_DETAILS,
            path: "organisations/:id",
            components: {
              tableLayoutColumn: () => import("./views/settings/organisations/OrganizationsView.vue"),
              detailsLayoutColumn: () => import("./views/settings/organisations/OrganizationDetails.vue"),
            },
            props: {
              tableLayoutColumn: true,
              detailsLayoutColumn: true,
            },
            meta: {
              rightBar: true,
              breadcrumb: $t("common.label.organization"),
            },
          },
          {
            name: Routes.ORGANISATION_DETAILS_CREATE,
            path: "organisations/create",
            components: {
              tableLayoutColumn: () => import("./views/settings/organisations/OrganizationsView.vue"),
              detailsLayoutColumn: () => import("./views/settings/organisations/OrganizationDetailsCreate.vue"),
            },
            meta: {
              rightBar: true,
              breadcrumb: $t("common.action.create"),
            },
          },
          ...filterRoutes({
            createName: Routes.ORGANISATION_FILTER_CREATE,
            updateName: Routes.ORGANISATION_FILTER_UPDATE,
            basePath: "organisations",
            listView: () => import("./views/settings/organisations/OrganizationsView.vue"),
          }),
          {
            name: Routes.SETTINGS_MODULE,
            path: "module/:moduleId",
            children: [
              {
                name: Routes.FOLDER_DETAILS,
                path: "/settings/module/:moduleId/folder/:id",
                components: {
                  tableLayoutColumn: () => import("./views/settings/template/TemplatesView.vue"),
                  detailsLayoutColumn: () => import("./views/settings/template/FolderDetailsUpdate.vue"),
                },
                props: {
                  tableLayoutColumn: true,
                  detailsLayoutColumn: true,
                },
                meta: {
                  breadcrumb: $t("common.label.details"),
                },
              },
              {
                name: Routes.FOLDER_DETAILS_CREATE,
                path: "/settings/module/:moduleId/folder/create",
                components: {
                  tableLayoutColumn: () => import("./views/settings/template/TemplatesView.vue"),
                  detailsLayoutColumn: () => import("./views/settings/template/FolderDetailsCreate.vue"),
                },
                props: {
                  tableLayoutColumn: true,
                  detailsLayoutColumn: true,
                },
                meta: {
                  breadcrumb: $t("common.action.createFolder"),
                },
              },
              {
                name: Routes.TEMPLATES,
                path: "/settings/module/:moduleId/templates",
                components: {
                  tableLayoutColumn: () => import("./views/settings/template/TemplatesView.vue"),
                },
                props: {
                  tableLayoutColumn: true,
                },
                meta: {
                  breadcrumb: $t("common.label.templates"),
                },
              },
              {
                name: Routes.TEMPLATE_DETAILS,
                path: "/settings/module/:moduleId/templates/:id",
                components: {
                  tableLayoutColumn: () => import("./views/settings/template/TemplatesView.vue"),
                  detailsLayoutColumn: () => import("./views/settings/template/TemplateDetailsUpdate.vue"),
                },
                props: {
                  tableLayoutColumn: true,
                  detailsLayoutColumn: true,
                },
                meta: {
                  rightBar: true,
                  breadcrumb: $t("common.label.details"),
                },
                children: [
                  {
                    name: Routes.TEMPLATE_FILE_EDITOR,
                    path: "file/:fileId/editor",
                    component: () => import("./views/OnlyOfficeEditorView.vue"),
                    props: (route) => ({ fileId: route.params.fileId as string, embedded: true }),
                    meta: {
                      rightBar: true,
                      breadcrumb: $t("fileExplorer.editor.label.title"),
                    },
                  },
                ],
              },
              {
                name: Routes.TEMPLATE_DETAILS_CREATE,
                path: "/settings/module/:moduleId/templates/create",
                components: {
                  tableLayoutColumn: () => import("./views/settings/template/TemplatesView.vue"),
                  detailsLayoutColumn: () => import("./views/settings/template/TemplateDetailsCreate.vue"),
                },
                props: {
                  tableLayoutColumn: true,
                  detailsLayoutColumn: true,
                },
                meta: {
                  rightBar: true,
                  breadcrumb: $t("common.action.create"),
                },
              },
              ...filterRoutes({
                createName: Routes.TEMPLATES_FILTER_CREATE,
                updateName: Routes.TEMPLATES_FILTER_UPDATE,
                basePath: "/settings/module/:moduleId/templates",
                listView: () => import("./views/settings/template/TemplatesView.vue"),
                extraProps: (route) => ({ moduleId: route.params.moduleId as string }),
              }),
              {
                name: Routes.TABLE_BAR,
                path: "table-bar",
                components: {
                  tableLayoutColumn: () => import("./views/settings/tableBar/TableBarView.vue"),
                },
                props: {
                  tableLayoutColumn: true,
                },
                meta: {
                  breadcrumb: $t("common.label.tableBar"),
                },
              },
            ],
          },
          {
            name: Routes.SETTINGS_PERMISSIONS,
            path: "permissions",
            meta: {
              breadcrumb: $t("common.label.permissions"),
            },
            children: [
              {
                name: Routes.SETTINGS_PERMISSIONS_ROLES,
                path: "roles",
                components: {
                  tableLayoutColumn: () => import("./views/settings/permissions/roles/RolesView.vue"),
                },
                props: {
                  tableLayoutColumn: true,
                },
                meta: {
                  breadcrumb: $t("common.label.roles"),
                },
              },
              {
                name: Routes.USER_GROUPS,
                path: "/settings/permissions/user-groups",
                components: {
                  tableLayoutColumn: () => import("./views/settings/permissions/usergroups/UserGroupsView.vue"),
                },
                props: {
                  tableLayoutColumn: true,
                },
                meta: {
                  breadcrumb: $t("common.label.userGroups"),
                },
              },
              {
                name: Routes.USER_GROUP_DETAILS,
                path: "/settings/permissions/user-groups/:id",
                components: {
                  tableLayoutColumn: () => import("./views/settings/permissions/usergroups/UserGroupsView.vue"),
                  detailsLayoutColumn: () => import("./views/settings/permissions/usergroups/UserGroupView.vue"),
                },
                props: {
                  tableLayoutColumn: true,
                  detailsLayoutColumn: true,
                },
                meta: {
                  rightBar: true,
                  breadcrumb: $t("common.label.details"),
                },
              },
              {
                name: Routes.USER_GROUP_DETAILS_CREATE,
                path: "/settings/permissions/user-groups/create",
                components: {
                  tableLayoutColumn: () => import("./views/settings/permissions/usergroups/UserGroupsView.vue"),
                  detailsLayoutColumn: () =>
                    import("./views/settings/permissions/usergroups/UserGroupDetailsCreate.vue"),
                },
                meta: {
                  rightBar: true,
                },
              },
              ...filterRoutes({
                createName: Routes.USER_GROUP_FILTER_CREATE,
                updateName: Routes.USER_GROUP_FILTER_UPDATE,
                basePath: "/settings/permissions/user-groups",
                listView: () => import("./views/settings/permissions/usergroups/UserGroupsView.vue"),
              }),
              {
                name: Routes.SETTINGS_PERMISSIONS_ROLES_CREATE,
                path: "roles/create",
                components: {
                  tableLayoutColumn: () => import("./views/settings/permissions/roles/RolesView.vue"),
                  detailsLayoutColumn: () => import("./views/settings/permissions/roles/RolesCreateView.vue"),
                },
                props: {
                  tableLayoutColumn: true,
                  detailsLayoutColumn: true,
                },
                meta: {
                  rightBar: true,
                  breadcrumb: $t("common.action.create"),
                },
              },
              {
                name: Routes.SETTINGS_PERMISSIONS_ROLES_DETAILS,
                path: "roles/:roleId",
                components: {
                  tableLayoutColumn: () => import("./views/settings/permissions/roles/RolesView.vue"),
                  detailsLayoutColumn: () => import("./views/settings/permissions/roles/RolesDetailsView.vue"),
                },
                props: {
                  tableLayoutColumn: true,
                  detailsLayoutColumn: true,
                },
                meta: {
                  rightBar: true,
                  breadcrumb: $t("common.action.create"),
                },
              },
              ...filterRoutes({
                createName: Routes.SETTINGS_PERMISSIONS_ROLES_FILTER_CREATE,
                updateName: Routes.SETTINGS_PERMISSIONS_ROLES_FILTER_UPDATE,
                basePath: "roles",
                listView: () => import("./views/settings/permissions/roles/RolesView.vue"),
              }),
              {
                name: Routes.SETTINGS_PERMISSIONS_ROLE_2_USR_GRP,
                path: "roleUserGroup",
                components: {
                  tableLayoutColumn: () => import("./views/settings/permissions/roleUserGroups/RoleUserGroupsView.vue"),
                },
                props: {
                  tableLayoutColumn: true,
                },
                meta: {
                  breadcrumb: $t("common.label.roleUserGroups"),
                },
              },
              {
                name: Routes.SETTINGS_PERMISSIONS_ROLE_2_USR_GRP_USERGROUP_DETAILS,
                path: "roleUserGroup/group/:groupId",
                components: {
                  tableLayoutColumn: () => import("./views/settings/permissions/roleUserGroups/RoleUserGroupsView.vue"),
                  detailsLayoutColumn: () =>
                    import("./views/settings/permissions/roleUserGroups/UserGroupDetailsView.vue"),
                },
                props: {
                  tableLayoutColumn: true,
                  detailsLayoutColumn: true,
                },
                meta: {
                  breadcrumb: $t("common.label.userGroup2"),
                },
              },
              {
                name: Routes.SETTINGS_PERMISSIONS_ROLE_2_USR_GRP_USER_DETAILS,
                path: "roleUserGroup/user/:userId",
                components: {
                  tableLayoutColumn: () => import("./views/settings/permissions/roleUserGroups/RoleUserGroupsView.vue"),
                  detailsLayoutColumn: () => import("./views/settings/permissions/roleUserGroups/UserDetailsView.vue"),
                },
                props: {
                  tableLayoutColumn: true,
                  detailsLayoutColumn: true,
                },
                meta: {
                  breadcrumb: $t("common.label.user"),
                },
              },
              ...filterRoutes({
                createName: Routes.SETTINGS_PERMISSIONS_ROLE_2_USR_GRP_FILTER_CREATE,
                updateName: Routes.SETTINGS_PERMISSIONS_ROLE_2_USR_GRP_FILTER_UPDATE,
                basePath: "roleUserGroup",
                listView: () => import("./views/settings/permissions/roleUserGroups/RoleUserGroupsView.vue"),
              }),
            ],
          },
          {
            name: Routes.LAYOUT_SECTION_GROUPS,
            path: "/settings/layout-section-groups",
            components: {
              tableLayoutColumn: () => import("./views/settings/layoutSectionGroup/LayoutSectionGroupList.vue"),
            },
            props: {
              tableLayoutColumn: true,
            },
            meta: {
              breadcrumb: $t("common.label.sidebar"),
            },
          },
          {
            name: Routes.LAYOUT_SECTION_GROUP_DETAILS,
            path: "/settings/layout-section-groups/:id",
            components: {
              tableLayoutColumn: () => import("./views/settings/layoutSectionGroup/LayoutSectionGroupList.vue"),
              detailsLayoutColumn: () => import("./views/settings/layoutSectionGroup/LayoutSectionGroupUpdate.vue"),
            },
            props: {
              tableLayoutColumn: true,
              detailsLayoutColumn: true,
            },
            meta: {
              rightBar: true,
              breadcrumb: $t("common.label.details"),
            },
          },
          {
            name: Routes.LAYOUT_SECTION_GROUP_CREATE,
            path: "/settings/layout-section-groups/create",
            components: {
              tableLayoutColumn: () => import("./views/settings/layoutSectionGroup/LayoutSectionGroupList.vue"),
              detailsLayoutColumn: () => import("./views/settings/layoutSectionGroup/LayoutSectionGroupCreate.vue"),
            },
            props: {
              tableLayoutColumn: true,
              detailsLayoutColumn: true,
            },
            meta: {
              rightBar: true,
              breadcrumb: $t("common.action.create"),
            },
          },
          {
            name: Routes.LAYOUT_SECTION_GROUP_UPDATE,
            path: "/settings/layout-section-groups/:id/update",
            components: {
              tableLayoutColumn: () => import("./views/settings/layoutSectionGroup/LayoutSectionGroupList.vue"),
              detailsLayoutColumn: () => import("./views/settings/layoutSectionGroup/LayoutSectionGroupUpdate.vue"),
            },
            props: {
              tableLayoutColumn: true,
              detailsLayoutColumn: true,
            },
            meta: {
              rightBar: true,
              breadcrumb: $t("common.action.update"),
            },
          },
          ...filterRoutes({
            createName: Routes.LAYOUT_SECTION_GROUP_FILTER_CREATE,
            updateName: Routes.LAYOUT_SECTION_GROUP_FILTER_UPDATE,
            basePath: "/settings/layout-section-groups",
            listView: () => import("./views/settings/layoutSectionGroup/LayoutSectionGroupList.vue"),
          }),
          ...filterRoutes({
            createName: Routes.LAYOUT_SECTION_GROUP_LOCATED_IN_FILTER_CREATE,
            updateName: Routes.LAYOUT_SECTION_GROUP_LOCATED_IN_FILTER_UPDATE,
            basePath: "/settings/layout-section-groups/:id/located-in",
            listView: () => import("./views/settings/layoutSectionGroup/LayoutSectionGroupList.vue"),
          }),
          {
            name: Routes.SETTINGS_AI,
            path: "/settings/ai",
            meta: {
              breadcrumb: $t("common.label.aiSettings"),
              // Without this the pages are reachable by URL for users the nav hides them from (#3230).
              entityPermission: "ai_settings",
            },
            children: [
              {
                name: Routes.SETTINGS_AI_COMMON,
                path: "/settings/ai/common",
                components: {
                  tableLayoutColumn: () => import("./views/settings/aiSettings/CommonView.vue"),
                },
                props: {
                  tableLayoutColumn: true,
                },
                meta: {
                  rightBar: true,
                  breadcrumb: $t("settings.aiSettings.label.common"),
                },
              },
              {
                name: Routes.SETTINGS_AI_FILE_CLASSIFICATION,
                path: "/settings/ai/file-classification",
                components: {
                  tableLayoutColumn: () => import("./views/settings/aiSettings/FileClassificationView.vue"),
                },
                props: {
                  tableLayoutColumn: true,
                },
                meta: {
                  rightBar: true,
                  breadcrumb: $t("settings.aiSettings.label.fileClassification"),
                },
              },
            ],
          },
          {
            name: Routes.SETTINGS_SERVICES,
            path: "/settings/services",
            meta: {
              breadcrumb: $t("common.label.services"),
            },
            children: [
              {
                name: Routes.SETTINGS_SERVICE_INTEGRATION,
                path: "/settings/services/integration",
                components: {
                  tableLayoutColumn: () => import("./views/settings/services/integration/IntegrationView.vue"),
                },
                props: {
                  tableLayoutColumn: true,
                },
                meta: {
                  breadcrumb: $t("common.label.integrations"),
                },
              },
              {
                name: Routes.SETTINGS_SERVICE_INTEGRATION_DETAILS,
                path: "/settings/services/integration/:id",
                components: {
                  tableLayoutColumn: () => import("./views/settings/services/integration/IntegrationView.vue"),
                  detailsLayoutColumn: () =>
                    import("./views/settings/services/integration/IntegrationDetailsUpdate.vue"),
                },
                props: {
                  tableLayoutColumn: true,
                  detailsLayoutColumn: true,
                },
                meta: {
                  rightBar: true,
                  breadcrumb: $t("common.label.details"),
                },
              },
              {
                name: Routes.SETTINGS_SERVICE_INTEGRATION_DETAILS_CREATE,
                path: "/settings/services/integration/create",
                components: {
                  tableLayoutColumn: () => import("./views/settings/services/integration/IntegrationView.vue"),
                  detailsLayoutColumn: () =>
                    import("./views/settings/services/integration/IntegrationDetailsCreate.vue"),
                },
                props: {
                  tableLayoutColumn: true,
                  detailsLayoutColumn: true,
                },
                meta: {
                  rightBar: true,
                  breadcrumb: $t("common.action.create"),
                },
              },
              ...filterRoutes({
                createName: Routes.SETTINGS_SERVICE_INTEGRATION_FILTER_CREATE,
                updateName: Routes.SETTINGS_SERVICE_INTEGRATION_FILTER_UPDATE,
                basePath: "/settings/services/integration",
                listView: () => import("./views/settings/services/integration/IntegrationView.vue"),
              }),
              {
                name: Routes.SETTINGS_SERVICE_INTEGRATION_RESOURCE_DETAILS_CREATE,
                path: "/settings/services/integration/:integrationId/resource/create",
                components: {
                  tableLayoutColumn: () => import("./views/settings/services/integration/IntegrationView.vue"),
                  detailsLayoutColumn: () => import("./views/settings/services/resource/ResourceDetailsCreate.vue"),
                },
                props: {
                  tableLayoutColumn: true,
                  detailsLayoutColumn: (route) => ({
                    ...route.params,
                    isFileLoader: true,
                  }),
                },
                meta: {
                  rightBar: true,
                  breadcrumb: $t("common.action.create"),
                },
              },
              {
                name: Routes.SETTINGS_SERVICE_INTEGRATION_RESOURCE_DETAILS,
                path: "/settings/services/integration/:integrationId/resource/:id",
                components: {
                  tableLayoutColumn: () => import("./views/settings/services/integration/IntegrationView.vue"),
                  detailsLayoutColumn: () => import("./views/settings/services/resource/ResourceDetailsUpdate.vue"),
                },
                props: {
                  tableLayoutColumn: true,
                  detailsLayoutColumn: (route) => ({
                    ...route.params,
                    isFileLoader: true,
                  }),
                },
                meta: {
                  rightBar: true,
                  breadcrumb: $t("common.label.details"),
                },
              },
              ...filterRoutes({
                createName: Routes.SETTINGS_SERVICE_INTEGRATION_ACCOUNTS_FILTER_CREATE,
                updateName: Routes.SETTINGS_SERVICE_INTEGRATION_ACCOUNTS_FILTER_UPDATE,
                basePath: "/settings/services/integration/:id/accounts",
                listView: () => import("./views/settings/services/integration/IntegrationView.vue"),
              }),
              ...filterRoutes({
                createName: Routes.SETTINGS_SERVICE_INTEGRATION_RESOURCES_FILTER_CREATE,
                updateName: Routes.SETTINGS_SERVICE_INTEGRATION_RESOURCES_FILTER_UPDATE,
                basePath: "/settings/services/integration/:integrationId/resource-filter",
                listView: () => import("./views/settings/services/integration/IntegrationView.vue"),
              }),
              {
                name: Routes.SETTINGS_SERVICE_ACCOUNT,
                path: "/settings/services/account",
                components: {
                  tableLayoutColumn: () => import("./views/settings/services/account/AccountView.vue"),
                },
                props: {
                  tableLayoutColumn: true,
                },
                meta: {
                  breadcrumb: $t("common.label.accounts"),
                },
              },
              {
                name: Routes.SETTINGS_SERVICE_ACCOUNT_DETAILS,
                path: "/settings/services/account/:id",
                components: {
                  tableLayoutColumn: () => import("./views/settings/services/account/AccountView.vue"),
                  detailsLayoutColumn: () => import("./views/settings/services/account/AccountDetailsUpdate.vue"),
                },
                props: {
                  tableLayoutColumn: true,
                  detailsLayoutColumn: true,
                },
                meta: {
                  rightBar: true,
                  breadcrumb: $t("common.label.details"),
                },
              },
              {
                name: Routes.SETTINGS_SERVICE_ACCOUNT_DETAILS_CREATE,
                path: "/settings/services/account/create",
                components: {
                  tableLayoutColumn: () => import("./views/settings/services/account/AccountView.vue"),
                  detailsLayoutColumn: () => import("./views/settings/services/account/AccountDetailsCreate.vue"),
                },
                props: {
                  tableLayoutColumn: true,
                  detailsLayoutColumn: true,
                },
                meta: {
                  rightBar: true,
                  breadcrumb: $t("common.action.create"),
                },
              },
              ...filterRoutes({
                createName: Routes.SETTINGS_SERVICE_ACCOUNT_FILTER_CREATE,
                updateName: Routes.SETTINGS_SERVICE_ACCOUNT_FILTER_UPDATE,
                basePath: "/settings/services/account",
                listView: () => import("./views/settings/services/account/AccountView.vue"),
              }),
              {
                name: Routes.SETTINGS_SERVICE_RESOURCE_DETAILS,
                path: "/settings/services/account/:accountId/resource/:id",
                components: {
                  tableLayoutColumn: () => import("./views/settings/services/account/AccountView.vue"),
                  detailsLayoutColumn: () => import("./views/settings/services/resource/ResourceDetailsUpdate.vue"),
                },
                props: {
                  tableLayoutColumn: true,
                  detailsLayoutColumn: true,
                },
                meta: {
                  rightBar: true,
                  breadcrumb: $t("common.label.details"),
                },
              },
              {
                name: Routes.SETTINGS_SERVICE_RESOURCE_DETAILS_CREATE,
                path: "/settings/services/account/:accountId/resource/create",
                components: {
                  tableLayoutColumn: () => import("./views/settings/services/account/AccountView.vue"),
                  detailsLayoutColumn: () => import("./views/settings/services/resource/ResourceDetailsCreate.vue"),
                },
                props: {
                  tableLayoutColumn: true,
                  detailsLayoutColumn: true,
                },
                meta: {
                  rightBar: true,
                  breadcrumb: $t("common.action.create"),
                },
              },
              ...filterRoutes({
                createName: Routes.SETTINGS_SERVICE_ACCOUNT_RESOURCES_FILTER_CREATE,
                updateName: Routes.SETTINGS_SERVICE_ACCOUNT_RESOURCES_FILTER_UPDATE,
                basePath: "/settings/services/account/:accountId/resource-filter",
                listView: () => import("./views/settings/services/account/AccountView.vue"),
              }),
            ],
          },
          {
            name: Routes.SETTINGS_USERS,
            path: "users",
            components: {
              tableLayoutColumn: () => import("./views/settings/users/UsersView.vue"),
            },
            props: {
              tableLayoutColumn: true,
            },
            meta: {
              breadcrumb: $t("common.label.users"),
            },
          },
          {
            name: Routes.USER_DETAILS,
            path: "users/:id",
            components: {
              tableLayoutColumn: () => import("./views/settings/users/UsersView.vue"),
              detailsLayoutColumn: () => import("./views/settings/users/UserDetailsView.vue"),
            },
            props: {
              tableLayoutColumn: true,
              detailsLayoutColumn: true,
            },
            meta: {
              rightBar: true,
              breadcrumb: $t("common.label.userDetails"),
            },
          },
          {
            name: Routes.USER_DETAILS_CREATE,
            path: "users/create",
            components: {
              tableLayoutColumn: () => import("./views/settings/users/UsersView.vue"),
              detailsLayoutColumn: () => import("./views/settings/users/UserDetailsCreate.vue"),
            },
            props: {
              tableLayoutColumn: true,
              detailsLayoutColumn: true,
            },
            meta: {
              rightBar: true,
              breadcrumb: $t("common.action.createUser"),
            },
          },
          ...filterRoutes({
            createName: Routes.USER_FILTER_CREATE,
            updateName: Routes.USER_FILTER_UPDATE,
            basePath: "users",
            listView: () => import("./views/settings/users/UsersView.vue"),
          }),
          ...filterRoutes({
            createName: Routes.USER_ASSIGNED_USER_GROUPS_FILTER_CREATE,
            updateName: Routes.USER_ASSIGNED_USER_GROUPS_FILTER_UPDATE,
            basePath: "users/:id/groups",
            listView: () => import("./views/settings/users/UsersView.vue"),
          }),
          ...filterRoutes({
            createName: Routes.USER_INDIVIDUAL_PERMISSIONS_FILTER_CREATE,
            updateName: Routes.USER_INDIVIDUAL_PERMISSIONS_FILTER_UPDATE,
            basePath: "users/:id/individual-permissions",
            listView: () => import("./views/settings/users/UsersView.vue"),
          }),
          {
            name: Routes.SETTINGS_CUSTOM_DATA,
            path: "custom-data",
            meta: {
              breadcrumb: $t("common.label.customData"),
            },
            children: [
              {
                name: Routes.TENANT_PUBLIC_CATEGORIES,
                path: "/settings/custom-data/public-categories",
                components: {
                  tableLayoutColumn: () => import("./views/settings/tenant/PublicCategoriesView.vue"),
                },
                props: {
                  tableLayoutColumn: true,
                },
                meta: {
                  breadcrumb: $t("common.label.publicCategories"),
                },
              },
              {
                name: Routes.TENANT_PUBLIC_CATEGORY_DETAILS,
                path: "/settings/custom-data/public-categories/:id",
                components: {
                  tableLayoutColumn: () => import("./views/settings/tenant/PublicCategoriesView.vue"),
                  detailsLayoutColumn: () => import("./views/settings/tenant/PublicCategoryView.vue"),
                },
                props: {
                  tableLayoutColumn: true,
                  detailsLayoutColumn: true,
                },
                meta: {
                  rightBar: true,
                  breadcrumb: $t("common.label.publicCategory"),
                },
              },
              {
                name: Routes.TENANT_PUBLIC_CATEGORY_DETAILS_CREATE,
                path: "/settings/custom-data/public-categories/create",
                components: {
                  tableLayoutColumn: () => import("./views/settings/tenant/PublicCategoriesView.vue"),
                  detailsLayoutColumn: () => import("./views/settings/tenant/PublicCategoryDetailsCreate.vue"),
                },
                props: {
                  tableLayoutColumn: true,
                  detailsLayoutColumn: true,
                },
                meta: {
                  rightBar: true,
                  breadcrumb: $t("common.action.create"),
                },
              },
              ...filterRoutes({
                createName: Routes.TENANT_PUBLIC_CATEGORY_FILTER_CREATE,
                updateName: Routes.TENANT_PUBLIC_CATEGORY_FILTER_UPDATE,
                basePath: "/settings/custom-data/public-categories",
                listView: () => import("./views/settings/tenant/PublicCategoriesView.vue"),
              }),
              {
                name: Routes.CUSTOM_FIELDS,
                path: "/settings/custom-data/customfields",
                components: {
                  tableLayoutColumn: () => import("./views/settings/customfield/CustomFieldsView.vue"),
                },
                props: {
                  tableLayoutColumn: true,
                },
                meta: {
                  breadcrumb: $t("common.label.customFields"),
                },
              },
              {
                name: Routes.CUSTOM_FIELD_DETAILS,
                path: "/settings/custom-data/customfields/:id",
                components: {
                  tableLayoutColumn: () => import("./views/settings/customfield/CustomFieldsView.vue"),
                  detailsLayoutColumn: () => import("./views/settings/customfield/CustomFieldDetailsUpdate.vue"),
                },
                props: {
                  tableLayoutColumn: true,
                  detailsLayoutColumn: true,
                },
                meta: {
                  rightBar: true,
                  breadcrumb: $t("common.label.details"),
                },
              },
              {
                name: Routes.CUSTOM_FIELD_DETAILS_CREATE,
                path: "/settings/custom-data/customfields/create",
                components: {
                  tableLayoutColumn: () => import("./views/settings/customfield/CustomFieldsView.vue"),
                  detailsLayoutColumn: () => import("./views/settings/customfield/CustomFieldDetailsCreate.vue"),
                },
                props: {
                  tableLayoutColumn: true,
                  detailsLayoutColumn: true,
                },
                meta: {
                  rightBar: true,
                  breadcrumb: $t("common.action.create"),
                },
              },
              ...filterRoutes({
                createName: Routes.CUSTOM_FIELDS_FILTER_CREATE,
                updateName: Routes.CUSTOM_FIELDS_FILTER_UPDATE,
                basePath: "/settings/custom-data/customfields",
                listView: () => import("./views/settings/customfield/CustomFieldsView.vue"),
              }),
              ...filterRoutes({
                createName: Routes.CUSTOM_FIELD_LOCATED_IN_FILTER_CREATE,
                updateName: Routes.CUSTOM_FIELD_LOCATED_IN_FILTER_UPDATE,
                basePath: "/settings/custom-data/customfields/:id/located-in",
                listView: () => import("./views/settings/customfield/CustomFieldsView.vue"),
              }),
              {
                name: Routes.FIELD_GROUPS,
                path: "/settings/custom-data/fieldgroups",
                components: {
                  tableLayoutColumn: () => import("./views/settings/fieldgroup/FieldGroupsView.vue"),
                },
                props: {
                  tableLayoutColumn: true,
                },
                meta: {
                  breadcrumb: $t("common.label.fieldGroups"),
                },
              },
              {
                name: Routes.FIELD_GROUP_DETAILS,
                path: "/settings/custom-data/fieldgroups/:id",
                components: {
                  tableLayoutColumn: () => import("./views/settings/fieldgroup/FieldGroupsView.vue"),
                  detailsLayoutColumn: () => import("./views/settings/fieldgroup/FieldGroupDetailsUpdate.vue"),
                },
                props: {
                  tableLayoutColumn: true,
                  detailsLayoutColumn: true,
                },
                meta: {
                  rightBar: true,
                  breadcrumb: $t("common.label.details"),
                },
              },
              {
                name: Routes.FIELD_GROUP_DETAILS_CREATE,
                path: "/settings/custom-data/fieldgroups/create",
                components: {
                  tableLayoutColumn: () => import("./views/settings/fieldgroup/FieldGroupsView.vue"),
                  detailsLayoutColumn: () => import("./views/settings/fieldgroup/FieldGroupDetailsCreate.vue"),
                },
                props: {
                  tableLayoutColumn: true,
                  detailsLayoutColumn: true,
                },
                meta: {
                  rightBar: true,
                  breadcrumb: $t("common.action.create"),
                },
              },
              ...filterRoutes({
                createName: Routes.FIELD_GROUP_FILTER_CREATE,
                updateName: Routes.FIELD_GROUP_FILTER_UPDATE,
                basePath: "/settings/custom-data/fieldgroups",
                listView: () => import("./views/settings/fieldgroup/FieldGroupsView.vue"),
              }),
              {
                name: Routes.CUSTOM_SEQUENCES,
                path: "/settings/custom-data/custom-sequences",
                components: {
                  tableLayoutColumn: () => import("@/views/settings/customSequence/CustomSequencesView.vue"),
                },
                props: {
                  tableLayoutColumn: true,
                },
                meta: {
                  breadcrumb: $t("common.label.scaylaIdNumber"),
                },
              },
              {
                name: Routes.CUSTOM_SEQUENCE_DETAILS,
                path: "/settings/custom-data/custom-sequences/:id",
                components: {
                  tableLayoutColumn: () => import("./views/settings/customSequence/CustomSequencesView.vue"),
                  detailsLayoutColumn: () => import("./views/settings/customSequence/CustomSequencesDetailsUpdate.vue"),
                },
                props: {
                  tableLayoutColumn: true,
                  detailsLayoutColumn: true,
                },
                meta: {
                  rightBar: true,
                  breadcrumb: $t("common.label.details"),
                },
              },
              {
                name: Routes.CUSTOM_SEQUENCE_DETAILS_CREATE,
                path: "/settings/custom-data/custom-sequences/create",
                components: {
                  tableLayoutColumn: () => import("./views/settings/customSequence/CustomSequencesView.vue"),
                  detailsLayoutColumn: () => import("./views/settings/customSequence/CustomSequencesDetailsCreate.vue"),
                },
                props: {
                  tableLayoutColumn: true,
                  detailsLayoutColumn: true,
                },
                meta: {
                  rightBar: true,
                  breadcrumb: $t("common.action.create"),
                },
              },
              ...filterRoutes({
                createName: Routes.CUSTOM_SEQUENCE_FILTER_CREATE,
                updateName: Routes.CUSTOM_SEQUENCE_FILTER_UPDATE,
                basePath: "/settings/custom-data/custom-sequences",
                listView: () => import("./views/settings/customSequence/CustomSequencesView.vue"),
              }),
              ...filterRoutes({
                createName: Routes.CUSTOM_SEQUENCE_LOCATED_IN_FILTER_CREATE,
                updateName: Routes.CUSTOM_SEQUENCE_LOCATED_IN_FILTER_UPDATE,
                basePath: "/settings/custom-data/custom-sequences/:id/located-in",
                listView: () => import("./views/settings/customSequence/CustomSequencesView.vue"),
              }),
              {
                name: Routes.TENANT_RECORD_STATUS_SETS,
                path: "/settings/custom-data/recordstatussets",
                components: {
                  tableLayoutColumn: () =>
                    import("./views/settings/tenant/recordattributeset/RecordAttributeSetsView.vue"),
                },
                props: {
                  tableLayoutColumn: () => ({ attribute: "status" }),
                },
                meta: {
                  breadcrumb: $t("common.label.statusSets"),
                },
              },
              {
                name: Routes.TENANT_RECORD_STATUS_SET_DETAILS,
                path: "/settings/custom-data/recordstatussets/:id",
                components: {
                  tableLayoutColumn: () =>
                    import("./views/settings/tenant/recordattributeset/RecordAttributeSetsView.vue"),
                  detailsLayoutColumn: () =>
                    import("./views/settings/tenant/recordattributeset/RecordAttributeSetDetails.vue"),
                },
                props: {
                  tableLayoutColumn: () => ({ attribute: "status" }),
                  detailsLayoutColumn: (route) => ({
                    attribute: "status",
                    id: route.params.id,
                  }),
                },
                meta: {
                  rightBar: true,
                  breadcrumb: $t("common.label.statusSet"),
                },
              },
              {
                name: Routes.TENANT_RECORD_STATUS_SET_DETAILS_CREATE,
                path: "/settings/custom-data/recordstatussets/create",
                components: {
                  tableLayoutColumn: () =>
                    import("./views/settings/tenant/recordattributeset/RecordAttributeSetsView.vue"),
                  detailsLayoutColumn: () =>
                    import("./views/settings/tenant/recordattributeset/RecordAttributeSetDetailsCreate.vue"),
                },
                props: {
                  tableLayoutColumn: () => ({ attribute: "status" }),
                  detailsLayoutColumn: () => ({ attribute: "status" }),
                },
                meta: {
                  rightBar: true,
                  breadcrumb: $t("common.action.create"),
                },
              },
              ...filterRoutes({
                createName: Routes.TENANT_RECORD_STATUS_SET_FILTER_CREATE,
                updateName: Routes.TENANT_RECORD_STATUS_SET_FILTER_UPDATE,
                basePath: "/settings/custom-data/recordstatussets",
                listView: () => import("./views/settings/tenant/recordattributeset/RecordAttributeSetsView.vue"),
                listProps: () => ({ attribute: "status" }),
              }),
              ...filterRoutes({
                createName: Routes.TENANT_RECORD_STATUS_SET_CONFIGURATION_FILTER_CREATE,
                updateName: Routes.TENANT_RECORD_STATUS_SET_CONFIGURATION_FILTER_UPDATE,
                basePath: "/settings/custom-data/recordstatussets/:id/configuration",
                listView: () => import("./views/settings/tenant/recordattributeset/RecordAttributeSetsView.vue"),
                listProps: () => ({ attribute: "status" }),
              }),
              ...filterRoutes({
                createName: Routes.TENANT_RECORD_STATUS_SET_LOCATED_IN_FILTER_CREATE,
                updateName: Routes.TENANT_RECORD_STATUS_SET_LOCATED_IN_FILTER_UPDATE,
                basePath: "/settings/custom-data/recordstatussets/:id/located-in",
                listView: () => import("./views/settings/tenant/recordattributeset/RecordAttributeSetsView.vue"),
                listProps: () => ({ attribute: "status" }),
              }),
              {
                name: Routes.TENANT_RECORD_PRIORITY_SETS,
                path: "/settings/custom-data/recordprioritysets",
                components: {
                  tableLayoutColumn: () =>
                    import("./views/settings/tenant/recordattributeset/RecordAttributeSetsView.vue"),
                },
                props: {
                  tableLayoutColumn: () => ({ attribute: "priority" }),
                },
                meta: {
                  breadcrumb: $t("common.label.prioritySets"),
                },
              },
              {
                name: Routes.TENANT_RECORD_PRIORITY_SET_DETAILS,
                path: "/settings/custom-data/recordprioritysets/:id",
                components: {
                  tableLayoutColumn: () =>
                    import("./views/settings/tenant/recordattributeset/RecordAttributeSetsView.vue"),
                  detailsLayoutColumn: () =>
                    import("./views/settings/tenant/recordattributeset/RecordAttributeSetDetails.vue"),
                },
                props: {
                  tableLayoutColumn: () => ({ attribute: "priority" }),
                  detailsLayoutColumn: (route) => ({
                    attribute: "priority",
                    id: route.params.id,
                  }),
                },
                meta: {
                  rightBar: true,
                  breadcrumb: $t("common.label.prioritySet"),
                },
              },
              {
                name: Routes.TENANT_RECORD_PRIORITY_SET_DETAILS_CREATE,
                path: "/settings/custom-data/recordprioritysets/create",
                components: {
                  tableLayoutColumn: () =>
                    import("./views/settings/tenant/recordattributeset/RecordAttributeSetsView.vue"),
                  detailsLayoutColumn: () =>
                    import("./views/settings/tenant/recordattributeset/RecordAttributeSetDetailsCreate.vue"),
                },
                props: {
                  tableLayoutColumn: () => ({ attribute: "priority" }),
                  detailsLayoutColumn: () => ({ attribute: "priority" }),
                },
                meta: {
                  rightBar: true,
                  breadcrumb: $t("common.action.create"),
                },
              },
              ...filterRoutes({
                createName: Routes.TENANT_RECORD_PRIORITY_SET_FILTER_CREATE,
                updateName: Routes.TENANT_RECORD_PRIORITY_SET_FILTER_UPDATE,
                basePath: "/settings/custom-data/recordprioritysets",
                listView: () => import("./views/settings/tenant/recordattributeset/RecordAttributeSetsView.vue"),
                listProps: () => ({ attribute: "priority" }),
              }),
              ...filterRoutes({
                createName: Routes.TENANT_RECORD_PRIORITY_SET_CONFIGURATION_FILTER_CREATE,
                updateName: Routes.TENANT_RECORD_PRIORITY_SET_CONFIGURATION_FILTER_UPDATE,
                basePath: "/settings/custom-data/recordprioritysets/:id/configuration",
                listView: () => import("./views/settings/tenant/recordattributeset/RecordAttributeSetsView.vue"),
                listProps: () => ({ attribute: "priority" }),
              }),
              ...filterRoutes({
                createName: Routes.TENANT_RECORD_PRIORITY_SET_LOCATED_IN_FILTER_CREATE,
                updateName: Routes.TENANT_RECORD_PRIORITY_SET_LOCATED_IN_FILTER_UPDATE,
                basePath: "/settings/custom-data/recordprioritysets/:id/located-in",
                listView: () => import("./views/settings/tenant/recordattributeset/RecordAttributeSetsView.vue"),
                listProps: () => ({ attribute: "priority" }),
              }),
            ],
          },
        ],
      },
      {
        path: "/user-settings",
        name: Routes.USER_SETTINGS,
        meta: {
          breadcrumb: $t("common.label.userSettings"),
        },
        children: [
          {
            name: Routes.USER_SETTINGS_PROFILE,
            path: "profile",
            components: {
              tableLayoutColumn: () => import("./views/userSettings/ProfileView.vue"),
            },
            props: {
              tableLayoutColumn: (route) => ({ recordId: route.query.recordId ?? undefined }),
            },
            meta: {
              breadcrumb: $t("common.label.profile"),
            },
            children: [],
          },
          {
            name: Routes.USER_SETTINGS_PRIVATE_CATEGORIES,
            path: "/user-settings/private-categories",
            components: {
              tableLayoutColumn: () => import("./views/userSettings/PrivateCategoriesView.vue"),
            },
            props: {
              tableLayoutColumn: true,
            },
            meta: {
              breadcrumb: $t("common.label.privateCategories"),
            },
          },
          {
            name: Routes.USER_SETTINGS_PRIVATE_CATEGORY_DETAILS,
            path: "/user-settings/private-categories/:id",
            components: {
              tableLayoutColumn: () => import("./views/userSettings/PrivateCategoriesView.vue"),
              detailsLayoutColumn: () => import("./views/userSettings/PrivateCategoryView.vue"),
            },
            props: {
              tableLayoutColumn: true,
              detailsLayoutColumn: true,
            },
            meta: {
              rightBar: true,
              breadcrumb: $t("common.label.privateCategory"),
            },
          },
          {
            name: Routes.USER_SETTINGS_PRIVATE_CATEGORY_DETAILS_CREATE,
            path: "/user-settings/private-categories/create",
            components: {
              tableLayoutColumn: () => import("./views/userSettings/PrivateCategoriesView.vue"),
              detailsLayoutColumn: () => import("./views/userSettings/PrivateCategoryDetailsCreate.vue"),
            },
            props: {
              tableLayoutColumn: true,
              detailsLayoutColumn: true,
            },
            meta: {
              rightBar: true,
              breadcrumb: $t("common.action.create"),
            },
          },
          ...filterRoutes({
            createName: Routes.USER_SETTINGS_PRIVATE_CATEGORY_FILTER_CREATE,
            updateName: Routes.USER_SETTINGS_PRIVATE_CATEGORY_FILTER_UPDATE,
            basePath: "/user-settings/private-categories",
            listView: () => import("./views/userSettings/PrivateCategoriesView.vue"),
          }),
        ],
      },
    ],
  },
  {
    name: Routes.RECORD_PRINT,
    path: "/print/:moduleId/:recordId",
    component: () => import("./views/record/RecordPrint.vue"),
    props: true,
  },
  {
    name: Routes.FILE_EDITOR,
    path: "/files/:fileId/editor",
    component: () => import("./views/OnlyOfficeEditorView.vue"),
    props: (route) => ({
      fileId: route.params.fileId as string,
      mode: route.query.mode as string | undefined,
      recordId: route.query.recordId as string | undefined,
      draftSetId: route.query.draftSetId as string | undefined,
      generation: route.query.generation as string | undefined,
      fromTemplatesFolder: !!route.query.fromTemplatesFolder,
    }),
  },
  {
    name: Routes.FILE_PREVIEW,
    path: "/files/:fileId/preview",
    component: () => import("./views/FilePreviewView.vue"),
    props: (route) => ({
      fileId: route.params.fileId as string,
      generation: route.query.generation as string | undefined,
    }),
  },
  {
    name: Routes.NOT_FOUND,
    path: "/:pathMatch(.*)*",
    component: NotFoundView,
  },
  {
    // Deliberately not in components/navigation/settings.ts — reachable only by typing the URL.
    // Gated by users.tenant_manager plus a system password, neither exposed to the frontend (same
    // non-exposure as the removed is_admin check), so there's no router-level meta to gate on —
    // the page itself prompts for the password and lets every /ai-prompt-admin request's own
    // 401/403 be the real enforcement.
    name: Routes.AI_PROMPT_ADMIN,
    path: "/ai-prompt-admin",
    component: () => import("./views/AiPromptAdminView.vue"),
  },
];

export const router = createRouter({
  history: createWebHistory(),
  routes,
});

// Routes carrying meta.entityPermission are only reachable with active+read access to that
// entity. The settings navigation shows the menu item on `active` alone (#3230); without `read`
// a user should still see that entry but get redirected away from the page itself (#3416).
// Without `active` at all the page isn't reachable either — direct URL access previously hit the
// page (and a downstream 500, since nothing there is gated for a fully de-permissioned actor).
// Returns undefined to let the route through.
async function deniedRedirect(
  to: RouteLocationNormalized,
  workspace: ReturnType<typeof useWorkspace>,
): Promise<{ name: Routes } | undefined> {
  const entity = to.matched.find((r) => r.meta?.entityPermission)?.meta?.entityPermission as string | undefined;
  if (!entity) return undefined;

  await workspace.waitForPermissions();

  const permissions = workspace.getEntityPermissions(entity);
  return permissions.active && permissions.read ? undefined : { name: Routes.HOME };
}

async function unavailableTenantRedirect(
  to: RouteLocationNormalized,
  workspace: ReturnType<typeof useWorkspace>,
): Promise<{ name: Routes; query: { tenant: string } } | undefined> {
  const urlTenant = to.query.tenant as string | undefined;
  if (!urlTenant || urlTenant === workspace.tenantId || !workspace.tenantId) return undefined;

  await workspace.waitForSelectableTenants();

  if (workspace.selectableTenants?.some((t) => t.id === urlTenant)) return undefined;
  return { name: Routes.HOME, query: { tenant: workspace.tenantId } };
}

async function guardRedirect(to: RouteLocationNormalized, workspace: ReturnType<typeof useWorkspace>) {
  return (await unavailableTenantRedirect(to, workspace)) ?? (await deniedRedirect(to, workspace));
}

router.beforeEach(async (to, from, next) => {
  if (to.matched.some((r) => r.meta?.public === true)) {
    return next();
  }
  const workspace = useWorkspace();
  const oidcStore = useOidcStore();
  const auth = useAuth();

  let user = oidcStore.state.value.user?.profile;
  if (!user) {
    try {
      await auth.autoAuthenticate();
      user = oidcStore.state.value.user?.profile;
      if (user) {
        // Wait for tenantId to be loaded before continuing
        await workspace.waitForTenantId();
        const denied = await guardRedirect(to, workspace);
        return denied ? next(denied) : next();
      } else {
        await auth.signinRedirect();
        return next(false);
      }
    } catch (err) {
      console.warn(err);
      await auth.signinRedirect();
      return next(false);
    }
  } else {
    // User is already authenticated — wait for tenantId to avoid loading errors
    try {
      await workspace.waitForTenantId();
      const denied = await guardRedirect(to, workspace);
      return denied ? next(denied) : next();
    } catch (err) {
      console.error(err);
      await auth.signoutRedirect();
      return next(false);
    }
  }
});

router.afterEach((to) => {
  const workspace = useWorkspace();

  const urlTenant = to.query.tenant as string | undefined;

  if (urlTenant && urlTenant !== workspace.tenantId) {
    workspace.setTenantId(urlTenant);
  } else if (!urlTenant && workspace.tenantId) {
    const url = new URL(window.location.href);
    url.searchParams.set("tenant", workspace.tenantId);
    window.history.replaceState(history.state, "", url.toString());
  }
});
